import {isLeft} from 'fp-ts/lib/Either';
import {
    Mixed,
    OutputOf,
    Props,
    Type,
    TypeOf,
    failure,
    identity,
    intersection,
    keyof,
    nullType,
    readonly,
    record,
    string,
    success,
    type,
    undefined as undefinedType,
    union,
} from 'io-ts';

export const literalUnion = <U extends string | symbol, T extends readonly U[]>(...literals: T) =>
    keyof(Object.fromEntries(literals.map(literal => [literal])) as Record<T[number], void>);

export const optional = <T extends Mixed>(codec: T) => union([codec, undefinedType]);

export const nullable = <T extends Mixed>(codec: T) => union([codec, nullType]);

export const readonlyType = <T extends Props>(codec: T) => readonly(type(codec));

export const extendType = <T extends Mixed, U extends Props>(base: T, extension: U) =>
    intersection([base, readonlyType(extension)]);

export const readonlyRecord = <T extends Mixed, U extends Mixed>(domain: T, coDomain: U) =>
    readonly(record(domain, coDomain));

export const dictionary = <T extends Mixed>(domain: T) =>
    record(string, domain) as Type<{[key: string]: TypeOf<T>}, {[key: string]: OutputOf<T>}>;

export function postprocess<T extends Mixed>(codec: T, process: (value: TypeOf<T>) => TypeOf<T>): Type<TypeOf<T>> {
    return new Type(
        codec.name,
        codec.is,
        (i, ctx) => {
            const s = codec.validate(i, ctx);
            if (isLeft(s)) return s;
            return success(process(s.right));
        },
        codec.encode
    );
}

export function transform<T, U, O>(sourceCodec: Type<T>, targetCodec: Type<U, O>, transform: (value: T) => unknown) {
    return new Type(
        `${sourceCodec.name}To${targetCodec.name}`,
        targetCodec.is,
        (i, ctx) => {
            const s = sourceCodec.validate(i, ctx);
            if (isLeft(s)) return s;
            const t = targetCodec.validate(transform(s.right), ctx);
            if (isLeft(t)) return t;
            return t;
        },
        targetCodec.encode
    );
}

export function valueOf<T extends Record<keyof any, unknown>>(object: T): Type<T[keyof T]> {
    const isValue = (value: unknown): value is T[keyof T] => {
        return Object.values(object).includes(value);
    };

    return new Type<T[keyof T]>(
        `ValueOf`,
        isValue,
        (i, ctx) => {
            if (isValue(i)) {
                return success(i);
            }
            return failure(i, ctx);
        },
        identity
    );
}
